import { BASE_URL } from '../components/AppConfig';
import { getBearerToken } from '../utils/authToken';
import { isMissionsAuthError, isMissionsApiSuccess } from './missionsApi';

const authFetch = async (path, options = {}) => {
  const token = getBearerToken();
  const { headers: optionHeaders, ...rest } = options;
  const headers = {
    'Content-Type': 'application/json',
    ...(optionHeaders || {}),
  };
  if (token) {
    headers.Authorization = token;
  }

  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      ...rest,
      headers,
    });
    const json = await res.json().catch(() => ({}));
    json.http_status = res.status;
    return json;
  } catch {
    return { statusCode: '50000', message: 'Request failed' };
  }
};

const authFetchWithRetry = async (path, options = {}) => {
  let res = await authFetch(path, options);
  if (isMissionsAuthError(res)) {
    await new Promise((resolve) => setTimeout(resolve, 800));
    res = await authFetch(path, options);
  }
  return res;
};

export const isDailyPageApiSuccess = isMissionsApiSuccess;

export const getDailyPagePayload = (res) => res?.data ?? null;

export const getDailyPageErrorMessage = (res, fallback) => {
  if (isMissionsAuthError(res)) {
    return 'Session token is invalid or expired. Log out, log in again, then reopen this page.';
  }
  const details = (Array.isArray(res?.data?.errors) ? res.data.errors : [])
    .map((error) => [error?.path, error?.message].filter(Boolean).join(': '))
    .filter(Boolean)
    .join(' ');
  const message = String(res?.message || '');
  if (details) return message ? `${message} ${details}` : details;
  return message || fallback;
};

// ── Schedule & day shell ─────────────────────────────────────────────

/** GET /admin/daily-page/schedule */
export const getDailyPageSchedule = async ({
  startDate,
  endDate,
  limit,
  offset,
} = {}) => {
  const params = new URLSearchParams();
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  if (limit != null) params.set('limit', String(limit));
  if (offset != null) params.set('offset', String(offset));
  const query = params.toString();
  return authFetchWithRetry(`/admin/daily-page/schedule${query ? `?${query}` : ''}`, {
    method: 'GET',
  });
};

/** POST /admin/daily-page/:date/ensure */
export const ensureDailyPageDate = async (date) =>
  authFetch(`/admin/daily-page/${date}/ensure`, { method: 'POST', body: '{}' });

/** GET /admin/daily-page/:date — day details aggregate (5 accordions) */
export const getDailyPageDay = async (date) =>
  authFetchWithRetry(`/admin/daily-page/${date}`, { method: 'GET' });

/** GET /admin/daily-page/:date/sections */
export const getDaySections = async (date) =>
  authFetchWithRetry(`/admin/daily-page/${date}/sections`, { method: 'GET' });

/**
 * PATCH /admin/daily-page/:date/sections
 * Body: sparse flat `{ fe_wheel: false }` or `{ sections_visible: { … } }`
 */
export const patchDaySections = async (date, body) =>
  authFetch(`/admin/daily-page/${date}/sections`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

// ── Activities CRUD (wired create/edit UI) ───────────────────────────

/** GET /admin/daily-page/days/:date → data.activities[] */
export const getDailyPageDayActivities = async (date) =>
  authFetchWithRetry(`/admin/daily-page/days/${date}`, { method: 'GET' });

/** POST /admin/daily-page/days/:date/activities */
export const createDailyPageActivity = async (date, body) =>
  authFetch(`/admin/daily-page/days/${date}/activities`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** PUT /admin/daily-page/activities/:id */
export const updateDailyPageActivity = async (id, body) =>
  authFetch(`/admin/daily-page/activities/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

/** DELETE /admin/daily-page/activities/:id → is_active: false */
export const deleteDailyPageActivity = async (id) =>
  authFetch(`/admin/daily-page/activities/${id}`, { method: 'DELETE' });

// ── FE Wheel templates ───────────────────────────────────────────────

/** GET /admin/daily-page/wheel/templates */
export const listWheelTemplates = async () =>
  authFetchWithRetry('/admin/daily-page/wheel/templates', { method: 'GET' });

/** POST /admin/daily-page/wheel/templates */
export const createWheelTemplate = async (body) =>
  authFetch('/admin/daily-page/wheel/templates', {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** PATCH /admin/daily-page/wheel/templates/:id */
export const updateWheelTemplate = async (id, body) =>
  authFetch(`/admin/daily-page/wheel/templates/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

/** DELETE /admin/daily-page/wheel/templates/:id */
export const deleteWheelTemplate = async (id) =>
  authFetch(`/admin/daily-page/wheel/templates/${id}`, { method: 'DELETE' });

// ── Per-date FE wheel ────────────────────────────────────────────────

/** GET /admin/daily-page/:date/fe-wheel */
export const getDateFeWheel = async (date) =>
  authFetchWithRetry(`/admin/daily-page/${date}/fe-wheel`, { method: 'GET' });

/** PATCH /admin/daily-page/:date/fe-wheel */
export const assignDateFeWheel = async (date, body) =>
  authFetch(`/admin/daily-page/${date}/fe-wheel`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

/** DELETE /admin/daily-page/:date/fe-wheel */
export const unassignDateFeWheel = async (date) =>
  authFetch(`/admin/daily-page/${date}/fe-wheel`, { method: 'DELETE' });

// ── Global wheel ─────────────────────────────────────────────────────

/** GET /admin/daily-page/wheel */
export const getWheelConfig = async () =>
  authFetchWithRetry('/admin/daily-page/wheel', { method: 'GET' });

/** PUT /admin/daily-page/wheel (PATCH also accepted by BE) */
export const putWheelConfig = async (body) =>
  authFetch('/admin/daily-page/wheel', {
    method: 'PUT',
    body: JSON.stringify(body),
  });

// ── Reorder only (create/edit/delete use activities CRUD above) ──────

/** PATCH /admin/daily-page/:date/challenges/order */
export const reorderChallenges = async (date, items) =>
  authFetch(`/admin/daily-page/${date}/challenges/order`, {
    method: 'PATCH',
    body: JSON.stringify({ items }),
  });

/** PATCH /admin/daily-page/:date/opinion/order */
export const reorderOpinions = async (date, items) =>
  authFetch(`/admin/daily-page/${date}/opinion/order`, {
    method: 'PATCH',
    body: JSON.stringify({ items }),
  });

// ── Milestones ───────────────────────────────────────────────────────

/** GET /admin/daily-page/weekly-milestones */
export const getWeeklyMilestones = async () =>
  authFetchWithRetry('/admin/daily-page/weekly-milestones', { method: 'GET' });

/** PUT /admin/daily-page/weekly-milestones */
export const putWeeklyMilestones = async (body) =>
  authFetch('/admin/daily-page/weekly-milestones', {
    method: 'PUT',
    body: JSON.stringify(body),
  });

// ── Fan of the Day (read-only) ───────────────────────────────────────

/** GET /admin/daily-page/:date/fan-of-the-day */
export const getFanOfTheDay = async (date) =>
  authFetchWithRetry(`/admin/daily-page/${date}/fan-of-the-day`, { method: 'GET' });
